import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action } from "@ngrx/store";
import { Observable, of } from "rxjs/index";
import {
  catchError,
  filter,
  map,
  switchMap,
} from "rxjs/operators";
import { UserAction } from "./user.action";
import { UserService } from "./user.service";
import { AutoremindService } from "../../core/services/autoremind.service";
import { AccountSettingActions } from "../../account-setting/state/account-setting.actions";
import { UsersFeatureService } from "../../admin-user-features/services/users-feature.services";
import { FeaturePermission } from "../../core/model/user.model";

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private userService: UserService,
    private userFeaturesService: UsersFeatureService,
    private autoremindService: AutoremindService
  ) {}

  searchUsers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAction.searchUser),
      switchMap((action: any) =>
        this.userService.searchUser(action.searchPattern).pipe(
          map((user: any) => UserAction.searchUserSuccess({ user })),
          catchError((error) => of(UserAction.searchUserFail({ error })))
        )
      )
    )
  );

  getSelectedUserById$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAction.getSelectedUserById),
      switchMap((action: any) =>
        this.userService.getSelectedUserById(action.userId).pipe(
          switchMap((selectedUser: any) => [
            UserAction.getSelectedUserByIdSuccess({ selectedUser }),
          ]),
          catchError((error) => of(UserAction.searchUserFail({ error })))
        )
      )
    )
  );

  getTemplates$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAction.getTemplatesByUserId),
      switchMap(_ =>
        this.autoremindService.getTemplatesForAR3User().pipe(
          map((payload: any) =>
            UserAction.getTemplatesByUserIdSuccess({ payload })
          ),
          catchError((error) =>
            of(UserAction.getTemplatesByUserIdFail({ error }))
          )
        )
      )
    )
  );

  getSelectedUserByIdSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAction.getSelectedUserByIdSuccess),
      filter((action) => action.selectedUser !== undefined),
      map((action) =>
        AccountSettingActions.getAccountSettingsByUserId({
          user: action.selectedUser,
        })
      )
    )
  );

  getSelectedUserFeatures$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAction.getSelectedUserByIdSuccess),
      switchMap(({ selectedUser }) =>
        this.userFeaturesService.getPermissionTypeFeatures().pipe(
          map((featureServiceResponse) => {
            return featureServiceResponse.permissions.map<FeaturePermission>(
              (featureItem) => {
                return {
                  permissionId: featureItem.permissionId,
                  permissionKey: featureItem.permissionKey,
                };
              }
            );
          }),
          map((mappedFeatureList) => {
            return mappedFeatureList.filter((featureItem) => {
              const hasSomeMatch = selectedUser.userPermissions.find(
                (userPermission) =>
                  userPermission.permissionId === featureItem.permissionId
              );
              return Boolean(hasSomeMatch);
            });
          }),
          map((filteredFeatureList) =>
            UserAction.getUserFeaturesSuccess({
              features: filteredFeatureList,
            })
          ),
          catchError((error) => of(UserAction.getUserFeaturesFail(error)))
        )
      )
    )
  );

    getUserCustomFields$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(UserAction.getUserCustomFields),
            switchMap((action: any) =>
                this.userService.getUserCustomFields(action.userId).pipe(
                    switchMap((userCustomFields: any) => [
                        UserAction.getUserCustomFieldsSuccess({ userCustomFields: userCustomFields }),
                    ]),
                    catchError((error) => of(UserAction.searchUserFail({ error })))
                )
            )
        )
    );
}
